/**
 * Shared helpers for the animated marketing components
 * (`src/components/marketing/animated/`).
 *
 * Every animation in this directory is a PURE function of the 0..1
 * progress emitted by `useStageDriver` (see `@/lib/motion`) — these
 * helpers are the pure math + the shared font/easing constants. No
 * component here may own a setTimeout timeline.
 */

import type { StageFrame } from "@/lib/motion";

/** Marketing mono stack — `--font-mono` is declared in design-tokens.css. */
export const MONO =
  "var(--font-mono, ui-monospace, Menlo, monospace)" as const;

/**
 * Serif display stack for the editorial headings/numerals the prototypes
 * set in Newsreader. Newsreader is not loaded yet (fonts live in
 * layout.tsx, outside this batch's territory) — `--font-serif` is a
 * forward hook: if the page-wiring batch adds a Next/Font Newsreader and
 * exposes it as `--font-serif`, these components pick it up with zero
 * changes. Until then Georgia renders the same editorial register.
 */
export const SERIF =
  "var(--font-serif, Georgia, 'Times New Roman', serif)" as const;

/** The one project easing (mirrors `--wtm-ease` in globals.css). */
export const EASE = "cubic-bezier(0.22, 1, 0.36, 1)" as const;

/** Prototype hairline colors (slate at .14 / .28 alpha). */
export const LINE = "rgba(148,163,184,0.14)" as const;
export const LINE_STRONG = "rgba(148,163,184,0.28)" as const;

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Local progress 0..1 for stage `i` given the driver frame:
 * 0 before the stage starts, the in-stage progress while active,
 * 1 once the timeline has moved past it. The whole pipeline render
 * derives from these values — that is what makes it scrub-driver-safe.
 */
export function stageLocalProgress(frame: StageFrame, i: number): number {
  if (frame.stage > i) return 1;
  if (frame.stage < i) return 0;
  return frame.stageProgress;
}

/**
 * Re-window a 0..1 progress: 0 until `start`, 1 at `start + span`,
 * linear in between. The building block for staggered sub-animations.
 */
export function window01(p: number, start: number, span: number): number {
  if (span <= 0) return p >= start ? 1 : 0;
  return clamp01((p - start) / span);
}
